/**
 * Operational form templates for the real-estate lifecycle.
 *
 * A template is only an initial draft. It is copied into the form editor and
 * remains account-owned after it is saved, so future catalogue changes never
 * modify an operator's published form.
 */
const choiceQuestion = (key, label, options, required = true) => ({
  key,
  type: 'single_choice',
  label,
  required,
  options: options.map(option => ({
    value: option.value,
    label: option.label,
  })),
});

const multiChoiceQuestion = (key, label, options, required = true) => ({
  key,
  type: 'multi_choice',
  label,
  required,
  options: options.map(option => ({
    value: option.value,
    label: option.label,
  })),
});

const textQuestion = (
  key,
  label,
  { required = true, long = false, placeholder = '' } = {}
) => ({
  key,
  type: long ? 'long_text' : 'text',
  label,
  required,
  options: [],
  max_length: long ? 1000 : 255,
  placeholder,
});

const structuredQuestion = (
  key,
  label,
  type,
  { required = true, placeholder = '', min = null, max = null } = {}
) => ({
  key,
  type,
  label,
  required,
  options: [],
  placeholder,
  min,
  max,
});

const booleanQuestion = (key, label, required = true) => ({
  key,
  type: 'boolean',
  label,
  required,
  options: [
    { value: 'true', label: 'Sim' },
    { value: 'false', label: 'Não' },
  ],
});

const template = ({
  id,
  icon,
  name,
  description,
  kind,
  operationalFlow,
  questions,
  success,
}) =>
  Object.freeze({
    id,
    icon,
    name,
    description,
    form: Object.freeze({
      name,
      kind,
      active: true,
      default_for_kind: false,
      description,
      questions: Object.freeze(questions),
      success_screen: Object.freeze({
        title: success,
        message:
          'Recebemos suas informações. Nossa equipe seguirá com o próximo passo.',
        cta_label: '',
        cta_url: '',
        show_score: false,
      }),
      settings: Object.freeze({
        expires_in_days: null,
        allow_resubmit: false,
        operational_flow: operationalFlow,
      }),
    }),
  });

export const FORM_TEMPLATES = Object.freeze([
  template({
    id: 'visit-scheduling',
    icon: 'i-lucide-calendar-clock',
    name: 'Pré-agendamento de visita',
    description:
      'Escolha um horário disponível e informe participantes e observações.',
    kind: 'visit',
    operationalFlow: 'visit_request',
    success: 'Seu pré-agendamento de visita foi recebido!',
    questions: [
      textQuestion('guests', 'Quem participará da visita?', { long: true }),
      textQuestion(
        'notes',
        'Quer compartilhar alguma observação para a visita?',
        { long: true, required: false }
      ),
    ],
  }),
  template({
    id: 'property-proposal',
    icon: 'i-lucide-file-signature',
    name: 'Envio de proposta',
    description:
      'Colete valor, composição de pagamento, crédito, documentos e prazo antes do fluxo de oferta.',
    kind: 'proposal',
    operationalFlow: 'property_offer',
    success: 'Sua proposta foi registrada!',
    questions: [
      structuredQuestion(
        'proposal_value',
        'Qual é o valor da proposta?',
        'number',
        { min: 0, placeholder: 'Ex.: 750000' }
      ),
      choiceQuestion('payment_method', 'Como pretende pagar?', [
        { value: 'cash', label: 'À vista' },
        { value: 'financing', label: 'Financiamento' },
        { value: 'fgts', label: 'FGTS e financiamento' },
      ]),
      structuredQuestion(
        'entry_amount',
        'Qual valor de entrada / recursos próprios?',
        'number',
        {
          required: false,
          min: 0,
          placeholder: 'Ex.: 150000',
        }
      ),
      structuredQuestion(
        'fgts_amount',
        'Qual valor pretende usar de FGTS?',
        'number',
        {
          required: false,
          min: 0,
          placeholder: 'Ex.: 50000',
        }
      ),
      structuredQuestion(
        'financed_amount',
        'Qual valor pretende financiar?',
        'number',
        {
          required: false,
          min: 0,
          placeholder: 'Ex.: 550000',
        }
      ),
      structuredQuestion(
        'earnest_payment_amount',
        'Haverá sinal ou reserva?',
        'number',
        {
          required: false,
          min: 0,
          placeholder: 'Ex.: 25000',
        }
      ),
      booleanQuestion('first_purchase', 'Este é seu primeiro imóvel?'),
      booleanQuestion('has_sfh', 'Pretende utilizar recursos do SFH?', false),
      choiceQuestion(
        'credit_status',
        'Como está seu crédito / financiamento?',
        [
          { value: 'to_simulate', label: 'Ainda vou simular' },
          { value: 'under_review', label: 'Em análise' },
          { value: 'pre_approved', label: 'Pré-aprovado' },
          { value: 'approved', label: 'Aprovado' },
        ],
        false
      ),
      choiceQuestion(
        'documents_status',
        'Como estão os documentos para a proposta?',
        [
          { value: 'ready', label: 'Tenho os documentos principais' },
          { value: 'collecting', label: 'Estou separando os documentos' },
          {
            value: 'needs_guidance',
            label: 'Preciso de orientação da imobiliária',
          },
        ],
        false
      ),
      structuredQuestion(
        'response_deadline_date',
        'Até quando a proposta pode ser respondida?',
        'date',
        { required: false }
      ),
      textQuestion(
        'proposal_terms',
        'Quais condições ou observações devemos considerar?',
        { long: true }
      ),
    ],
  }),
  template({
    id: 'kyc-documents',
    icon: 'i-lucide-shield-check',
    name: 'Solicitação de documentos (KYC)',
    description: 'Confirme dados e disponibilidade documental dos envolvidos.',
    kind: 'kyc',
    operationalFlow: 'kyc_request',
    success: 'Recebemos a confirmação dos documentos!',
    questions: [
      textQuestion('full_name', 'Nome completo conforme o documento'),
      choiceQuestion('document_type', 'Qual documento será enviado?', [
        { value: 'rg', label: 'RG' },
        { value: 'cnh', label: 'CNH' },
        { value: 'passport', label: 'Passaporte' },
      ]),
      structuredQuestion(
        'document_number',
        'Número do CPF ou CNPJ',
        'document_id'
      ),
      structuredQuestion('birth_date', 'Data de nascimento', 'date'),
      textQuestion('document_issuer', 'Órgão emissor do documento', {
        required: false,
        placeholder: 'Ex.: SSP/RJ',
      }),
      choiceQuestion(
        'document_status',
        'Como está a documentação para análise?',
        [
          { value: 'identity', label: 'Documentos de identidade prontos' },
          {
            value: 'documents_pending',
            label: 'Ainda estou reunindo documentos',
          },
          {
            value: 'needs_guidance',
            label: 'Preciso de orientação da imobiliária',
          },
        ]
      ),
      multiChoiceQuestion(
        'document_categories',
        'Quais comprovantes serão enviados nesta etapa?',
        [
          { value: 'identity', label: 'Documento de identificação' },
          { value: 'income', label: 'Comprovantes de renda' },
          { value: 'address', label: 'Comprovante de endereço' },
          { value: 'property', label: 'Documentos do imóvel' },
          { value: 'other', label: 'Outros documentos' },
        ],
        false
      ),
      booleanQuestion(
        'consent',
        'Autorizo o tratamento destes dados para análise da operação'
      ),
    ],
  }),
  template({
    id: 'visit-feedback',
    icon: 'i-lucide-message-square-heart',
    name: 'Feedback de visita',
    description:
      'Registre a avaliação do visitante e vincule a resposta à visita e ao imóvel corretos.',
    kind: 'visit_feedback',
    operationalFlow: 'visit_feedback',
    success: 'Seu feedback foi registrado!',
    questions: [
      textQuestion('body', 'Como foi a visita?', {
        long: true,
        placeholder: 'Conte o que funcionou e o que pode melhorar.',
      }),
      structuredQuestion('overall_rating', 'Nota geral', 'rating'),
      structuredQuestion(
        'performance_rating',
        'Como você avalia o atendimento do corretor?',
        'rating',
        { required: false }
      ),
      booleanQuestion(
        'would_schedule_again',
        'Você agendaria outra visita?',
        false
      ),
    ],
  }),
  template({
    id: 'property-listing',
    icon: 'i-lucide-building-2',
    name: 'Cadastro de imóvel',
    description: 'Receba informações iniciais para anunciar um imóvel.',
    kind: 'listing',
    operationalFlow: 'listing_intake',
    success: 'Recebemos os dados do imóvel!',
    questions: [
      textQuestion('address', 'Qual é o endereço do imóvel?'),
      choiceQuestion('purpose', 'Qual é a finalidade do anúncio?', [
        { value: 'rent', label: 'Aluguel' },
        { value: 'sale', label: 'Venda' },
        { value: 'both', label: 'Aluguel e venda' },
      ]),
      textQuestion(
        'property_details',
        'Conte os principais detalhes do imóvel',
        { long: true }
      ),
    ],
  }),
  template({
    id: 'buyer-qualification',
    icon: 'i-lucide-house-heart',
    name: 'Qualificação de comprador',
    description: 'Entenda orçamento, momento de compra e preferências.',
    kind: 'custom',
    operationalFlow: 'buyer_qualification',
    success: 'Recebemos seu perfil de compra!',
    questions: [
      structuredQuestion(
        'budget_max',
        'Qual é o orçamento máximo previsto?',
        'number',
        {
          min: 0,
          placeholder: 'Ex.: 500000',
        }
      ),
      structuredQuestion(
        'budget_min',
        'Existe um valor mínimo confortável?',
        'number',
        {
          required: false,
          min: 0,
          placeholder: 'Ex.: 400000',
        }
      ),
      choiceQuestion(
        'purchase_objective',
        'Qual é o objetivo principal da compra?',
        [
          { value: 'home', label: 'Morar' },
          { value: 'investment', label: 'Investir' },
        ]
      ),
      choiceQuestion('purchase_timing', 'Quando pretende comprar?', [
        { value: 'now', label: 'Agora' },
        { value: 'three_months', label: 'Nos próximos 3 meses' },
        { value: 'six_months', label: 'Nos próximos 6 meses' },
        { value: 'later', label: 'Ainda estou pesquisando' },
      ]),
      choiceQuestion(
        'payment_method',
        'Como pretende pagar?',
        [
          { value: 'cash', label: 'À vista' },
          { value: 'financing', label: 'Financiamento' },
          { value: 'fgts', label: 'FGTS e financiamento' },
        ],
        false
      ),
      booleanQuestion('first_purchase', 'Este é seu primeiro imóvel?', false),
      textQuestion('regions', 'Quais regiões você considera?', {
        placeholder: 'Ex.: Botafogo, Flamengo, Laranjeiras',
      }),
      multiChoiceQuestion('property_types', 'Quais tipos de imóvel aceita?', [
        { value: 'apartamento', label: 'Apartamento' },
        { value: 'casa', label: 'Casa' },
        { value: 'studio', label: 'Studio' },
        { value: 'cobertura', label: 'Cobertura' },
      ]),
      multiChoiceQuestion(
        'bedrooms',
        'Quantos quartos atendem à sua necessidade?',
        [
          { value: '1', label: '1 quarto' },
          { value: '2', label: '2 quartos' },
          { value: '3', label: '3 quartos' },
          { value: '4', label: '4 ou mais quartos' },
        ],
        false
      ),
      textQuestion(
        'preferences',
        'Quais regiões e características são essenciais?',
        { long: true, required: false }
      ),
    ],
  }),
  template({
    id: 'tenant-qualification',
    icon: 'i-lucide-key-round',
    name: 'Qualificação de locatário',
    description: 'Reúna renda, composição e prazo desejado para locação.',
    kind: 'custom',
    operationalFlow: 'tenant_qualification',
    success: 'Recebemos seu perfil de locação!',
    questions: [
      textQuestion('household', 'Quem vai morar no imóvel?', { long: true }),
      structuredQuestion(
        'rental_budget_max',
        'Qual é o orçamento máximo mensal para aluguel?',
        'number',
        { min: 0, placeholder: 'Ex.: 4500' }
      ),
      structuredQuestion(
        'rental_budget_min',
        'Existe um valor mensal mínimo confortável?',
        'number',
        { required: false, min: 0, placeholder: 'Ex.: 3500' }
      ),
      structuredQuestion(
        'income',
        'Qual é a renda mensal aproximada?',
        'number',
        { min: 0, placeholder: 'Ex.: 12000' }
      ),
      choiceQuestion('move_timing', 'Quando pretende se mudar?', [
        { value: 'immediately', label: 'Imediatamente' },
        { value: '30_days', label: 'Em até 30 dias' },
        { value: 'later', label: 'Em mais de 30 dias' },
      ]),
      textQuestion('regions', 'Quais regiões você considera?', {
        placeholder: 'Ex.: Botafogo, Flamengo, Laranjeiras',
      }),
      multiChoiceQuestion('property_types', 'Quais tipos de imóvel aceita?', [
        { value: 'apartamento', label: 'Apartamento' },
        { value: 'casa', label: 'Casa' },
        { value: 'studio', label: 'Studio' },
        { value: 'kitnet', label: 'Kitnet' },
      ]),
      multiChoiceQuestion(
        'bedrooms',
        'Quantos quartos atendem à sua necessidade?',
        [
          { value: '1', label: '1 quarto' },
          { value: '2', label: '2 quartos' },
          { value: '3', label: '3 quartos' },
          { value: '4', label: '4 ou mais quartos' },
        ],
        false
      ),
      textQuestion('preferences', 'Que características são essenciais?', {
        long: true,
        required: false,
      }),
    ],
  }),
  template({
    id: 'owner-onboarding',
    icon: 'i-lucide-user-round-check',
    name: 'Onboarding de proprietário',
    description: 'Inicie a preparação comercial e documental do proprietário.',
    kind: 'listing',
    operationalFlow: 'owner_onboarding',
    success: 'Recebemos as informações para iniciar o anúncio!',
    questions: [
      textQuestion('owner_name', 'Nome completo do proprietário'),
      textQuestion('property_address', 'Endereço do imóvel'),
      choiceQuestion(
        'ownership_status',
        'Como está a documentação do imóvel?',
        [
          { value: 'complete', label: 'Completa' },
          { value: 'pending', label: 'Há pendências' },
          { value: 'unknown', label: 'Preciso de orientação' },
        ]
      ),
    ],
  }),
  template({
    id: 'inspection',
    icon: 'i-lucide-clipboard-check',
    name: 'Vistoria',
    description: 'Registre condições, pendências e observações da vistoria.',
    kind: 'visit_feedback',
    operationalFlow: 'inspection',
    success: 'A vistoria foi registrada!',
    questions: [
      choiceQuestion('inspection_type', 'Qual é o tipo de vistoria?', [
        { value: 'entry', label: 'Entrada' },
        { value: 'exit', label: 'Saída' },
      ]),
      textQuestion('issues', 'Descreva pendências ou danos encontrados', {
        long: true,
      }),
      booleanQuestion(
        'access_confirmed',
        'O acesso ao imóvel foi realizado sem impedimentos'
      ),
    ],
  }),
  template({
    id: 'contract-signature',
    icon: 'i-lucide-pen-line',
    name: 'Assinatura de contrato',
    description:
      'Confirme os dados dos signatários antes do envio do contrato.',
    kind: 'custom',
    operationalFlow: 'contract_signature',
    success: 'Recebemos os dados para assinatura!',
    questions: [
      textQuestion('signatory_name', 'Nome completo do signatário'),
      structuredQuestion('signatory_email', 'E-mail para assinatura', 'email', {
        placeholder: 'nome@empresa.com',
      }),
      choiceQuestion(
        'signatory_role',
        'Qual é a sua participação no contrato?',
        [
          { value: 'tenant', label: 'Locatário' },
          { value: 'owner', label: 'Proprietário' },
          { value: 'guarantor', label: 'Fiador' },
          { value: 'buyer', label: 'Comprador' },
        ]
      ),
    ],
  }),
  template({
    id: 'maintenance-request',
    icon: 'i-lucide-wrench',
    name: 'Solicitação de manutenção',
    description:
      'Colete o problema, urgência e melhor horário para atendimento.',
    kind: 'support',
    operationalFlow: 'maintenance_request',
    success: 'Sua solicitação de manutenção foi registrada!',
    questions: [
      choiceQuestion('category', 'Qual área precisa de manutenção?', [
        { value: 'plumbing', label: 'Hidráulica' },
        { value: 'electrical', label: 'Elétrica' },
        { value: 'structural', label: 'Estrutural' },
        { value: 'appliance', label: 'Eletrodoméstico' },
        { value: 'other', label: 'Outra' },
      ]),
      choiceQuestion('urgency', 'Qual é a urgência?', [
        { value: 'emergency', label: 'Emergência' },
        { value: 'high', label: 'Alta' },
        { value: 'normal', label: 'Normal' },
      ]),
      textQuestion('issue_description', 'Descreva o que aconteceu', {
        long: true,
      }),
      choiceQuestion(
        'responsibility',
        'Quem deve avaliar a responsabilidade inicialmente?',
        [
          { value: 'tenant', label: 'Locatário' },
          { value: 'owner', label: 'Proprietário' },
          { value: 'agency', label: 'Imobiliária' },
          { value: 'unknown', label: 'Ainda não sei' },
        ]
      ),
      textQuestion('availability', 'Qual horário é melhor para atendimento?', {
        required: false,
      }),
    ],
  }),
  template({
    id: 'post-sale',
    icon: 'i-lucide-handshake',
    name: 'Acompanhamento pós-venda',
    description: 'Acompanhe a experiência após a conclusão da operação.',
    kind: 'post_sale',
    operationalFlow: 'post_sale_follow_up',
    success: 'Obrigado pelo seu retorno!',
    questions: [
      {
        key: 'satisfaction',
        type: 'rating',
        label: 'Como você avalia sua experiência?',
        required: true,
        options: [],
        scale: { min: 1, max: 5 },
      },
      textQuestion('feedback', 'O que podemos melhorar?', {
        long: true,
        required: false,
      }),
    ],
  }),
]);

export const findFormTemplate = templateId =>
  FORM_TEMPLATES.find(
    templateDefinition => templateDefinition.id === templateId
  );
